import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../lib/icons.js';
import { useHouse } from '../lib/store.js';

const HOLD_MS = 600;

/**
 * Press-and-hold to release the front door strike. The relay has its own
 * hardware timer, so a single turn_on is all the house needs — the hold is
 * purely so a pocket tap can never open the front door.
 */
export function DoorCard({
  entityId,
  contactEntityId,
}: {
  entityId: string;
  contactEntityId: string | null;
}): ReactNode {
  const { act, states } = useHouse();
  const [progress, setProgress] = useState(0);
  const [opened, setOpened] = useState(false);
  const rafRef = useRef<number>(0);
  const startRef = useRef(0);

  const doorOpen = contactEntityId !== null && states.get(contactEntityId)?.state === 'on';

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const begin = (): void => {
    if (opened) return;
    startRef.current = performance.now();
    const tick = (now: number): void => {
      const p = Math.min(1, (now - startRef.current) / HOLD_MS);
      setProgress(p);
      if (p >= 1) {
        setOpened(true);
        setProgress(0);
        void act({ action: 'unlock', entityId });
        window.setTimeout(() => setOpened(false), 6000);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const end = (): void => {
    cancelAnimationFrame(rafRef.current);
    setProgress(0);
  };

  return (
    <section className="glass hero">
      <div className="hero-top">
        <span className="hero-glyph" data-tone={opened ? 'positive' : doorOpen ? 'caution' : undefined}>
          <Icon name={opened ? 'unlock' : 'key'} size={22} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="hero-title">Front door</div>
          <div className="hero-sub">
            {opened
              ? 'Released — push the door now.'
              : doorOpen
                ? 'The front door is currently open.'
                : 'Hold the button to release the latch.'}
          </div>
        </div>
      </div>

      <p className="door-hint">
        You may need to pull then push the door after unlock to open it.
      </p>

      <button
        type="button"
        className="btn btn-primary btn-block holdbtn"
        onPointerDown={begin}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
        onContextMenu={(e) => e.preventDefault()}
        aria-label="Hold to unlock the front door"
      >
        <span className="fill" style={{ width: `${progress * 100}%` }} />
        <Icon name={opened ? 'check' : 'unlock'} size={18} />
        {opened ? 'Door released' : 'Hold to unlock'}
      </button>
    </section>
  );
}
